import toast from 'react-hot-toast';
import { windowHistoryBack, windowReloadPage, windowSoftRefresh } from './window-reload';

/** @readonly Toast icon types */
export const TOAST_ICONS = {
  success: '✅',
  warning: '⚠️',
  error: '❌',
  message: '💬',
  info: 'ℹ️',
  alert: '🚨',
};

/** @readonly Toast position enum (matches react-hot-toast) */
export const TOAST_POSITION = {
  TOP_LEFT: 'top-left',
  TOP_CENTER: 'top-center',
  TOP_RIGHT: 'top-right',
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM_CENTER: 'bottom-center',
  BOTTOM_RIGHT: 'bottom-right',
};

const useToast = () => {

  const defaultToastStyle = {
    width: 'max-content',
    maxWidth: '90vw',
    // background: '#f9fafb',        // light gray
    // color: '#111827',             // dark text
    // padding: '12px 16px',
    // borderRadius: '8px',
    // boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    // fontSize: '0.95rem',
    // fontWeight: 500,
  };

  // Use "Noop!" when nothing is happening as intended.
  // Use "Oops!" when an actual error has occurred.
  const toastError = (error, options = {}) => {
    const { duration = 3000, style = defaultToastStyle } = options;

    let msg = 'Lỗi xảy ra';
    if (error) {
      const { note, message, code } = error;
      msg = `${note ?? ''} ${message ?? code ?? 'Oops!'}`.trim();
    }

    toast.error(msg, { duration, style });
  };

  const toastSuccess = (msg = 'Success', options = {}) => {
    const { duration = 2000, action, style = defaultToastStyle } = options;

    toast.success(`${msg}`, { duration, style });

    switch (action) {
      case 'back':
        windowHistoryBack();
        break;
      case 'reload':
        windowReloadPage();
        break;
      case 'refresh':
        windowSoftRefresh();
        break;
      default:
        break;
    }
  };

  const toastMessage = (msg = 'Message', options = {}) => {
    const { duration = 2000, style = defaultToastStyle } = options;
    toast(`${msg}`, { duration, style });
  };

  
  /**
   * Generate a unique toast ID
   * @returns {string}
   */
  const generateToastId = () => `toast-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;

  /**
   * Show a custom toast with optional icon, ID, position, dismiss callback
   * @param {JSX.Element | string} children - Message content
   * @param {{
   *   duration?: number,
   *   style?: React.CSSProperties,
   *   id?: string,
   *   position?: keyof typeof TOAST_POSITION,
   *   icon?: string
   * }} options - Config for toast
   * @param {(toastId: string) => void} [onDismiss] - Dismiss handler
   */
  const toastCustom = (children, options = {}, onDismiss) => {
    const {
      duration = 4000,
      style = defaultToastStyle,
      id,
      position = TOAST_POSITION.TOP_CENTER,
      icon, // 👈 now use icon instead of type
    } = options;

    const toastId = id || generateToastId();

    toast.custom(
      (t) => (
        <div
          data-toast-id={toastId}
          onClick={() => toast.dismiss(t.id)}
          style={style}
        >
          {typeof children === 'string' ? (
            <span>
              {icon ? icon + ' ' : ''}{children}
            </span>
          ) : children}
        </div>
      ),
      {
        duration,
        id: toastId,
        position,
        onDismiss: () => {
          if (typeof onDismiss === 'function') {
            onDismiss(toastId);
          }
        },
      }
    );
  };
  
  /**
   * Show a loading toast and handle async process with optional success/error hooks
   * @param {string} msg - The loading message
   * @param {() => Promise<any>} onFunction - Async function to run
   * @param {{
   *   onSuccess?: (result: any, toastId: string) => void,
   *   onError?: (error: any, toastId: string) => void,
   *   id?: string,
   *   position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right',
   *   duration?: number,
   *   style?: React.CSSProperties
   * }} options
   */
  const toastLoading = async (
    msg = 'Đang xử lý ...',
    onFunction,
    options = {}
  ) => {
    const {
      onSuccess,
      onError,
      id,
      position = TOAST_POSITION.TOP_CENTER,
      duration = 2000,
      style = defaultToastStyle,
    } = options;

    const toastId = id || `toast-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;

    toast.loading(msg, {
      id: toastId,
      position,
      style,
    });

    if (typeof onFunction === 'function') {
      try {
        const result = await onFunction();
        if (typeof onSuccess === 'function') {
          onSuccess(result, toastId);
        }
      } catch (err) {
        if (typeof onError === 'function') {
          onError(err, toastId);
        }
      }
    } else {
      setTimeout(() => toast.dismiss(toastId), duration);
    }
  };

  return {
    toastError,
    toastSuccess,
    toastMessage,
    toastCustom,
    toastLoading,
  };
};

export default useToast;

/**
 * Usage Examples
toastCustom('Data saved!', { icon: TOAST_ICONS.success });
toastCustom('This is just text', {});
toastCustom(
  <strong>Custom JSX Content</strong>,
  { icon: TOAST_ICONS.info },
  (id) => console.log('Toast dismissed:', id)
);
 */

/**
 * Usage Examples
await toastLoading('Uploading...', async () => {
  await new Promise(res => setTimeout(res, 2000));
}, {
  onSuccess: (res, id) => toast.success('Uploaded!', { id }),
  position: TOAST_POSITION.BOTTOM_RIGHT,
  style: {
    ...defaultToastStyle,
    background: '#ecfdf5',
    color: '#065f46',
  },
});
 */
